/**
 * The announcement bar and popups (ADR-098): what the store says above every
 * page, and what it asks at the right moment — with the rules for when, kept
 * pure so the storefront and its tests read them the same way.
 */
// ─── Announcement bar ────────────────────────────────────────────────────────
export const ANNOUNCEMENT_TONES = ['brand', 'dark', 'sale', 'light'];
export const ANNOUNCEMENT_LIMITS = { messages: 3, text: 140, href: 500 };
export const ANNOUNCEMENT_DEFAULTS = {
    enabled: false,
    messages: [],
    tone: 'brand',
    dismissible: true,
    countdownTo: null,
    startsAt: null,
    endsAt: null,
};
/** The messages to show now in a language, or none: off, out of its dates, or nothing written. */
export function liveAnnouncement(setting, locale, now = new Date()) {
    if (!setting?.enabled)
        return [];
    if (setting.startsAt && Date.parse(setting.startsAt) > now.getTime())
        return [];
    if (setting.endsAt && Date.parse(setting.endsAt) <= now.getTime())
        return [];
    return (setting.messages ?? []).flatMap((message) => {
        const text = (message[locale] || message.ar || message.en || '').trim();
        return text ? [{ text, href: message.href?.trim() || null }] : [];
    });
}
/** What a countdown shows: whole days, hours, minutes, seconds left; null once it is over. */
export function countdownParts(to, now = new Date()) {
    const end = to ? Date.parse(to) : Number.NaN;
    if (Number.isNaN(end) || end <= now.getTime())
        return null;
    const left = Math.floor((end - now.getTime()) / 1000);
    return { days: Math.floor(left / 86_400), hours: Math.floor((left % 86_400) / 3600), minutes: Math.floor((left % 3600) / 60), seconds: left % 60 };
}
/** The bar's version: a dismissal holds until the words change. */
export function announcementVersion(setting) {
    let hash = 0;
    for (const char of JSON.stringify(setting.messages))
        hash = (hash * 31 + char.charCodeAt(0)) | 0;
    return (hash >>> 0).toString(36);
}
// ─── Popups ──────────────────────────────────────────────────────────────────
export const POPUP_KINDS = ['NEWSLETTER', 'OFFER', 'NOTICE'];
export const POPUP_TRIGGERS = ['DELAY', 'SCROLL', 'EXIT'];
/** Where a popup may appear. Checkout, orders and the account are never among them. */
export const POPUP_PAGES = ['all', 'home', 'product', 'category', 'cart', 'blog', 'pages'];
export const POPUP_AUDIENCES = ['all', 'new', 'returning'];
export const POPUP_DEVICES = ['all', 'mobile', 'desktop'];
export const POPUP_EVENTS = ['view', 'click'];
export const POPUP_LIMITS = { name: 120, title: 120, body: 600, cta: 40, href: 500, triggerSeconds: 120, frequencyDays: 365 };
/** The kind of page a path is, for a popup's pages; `blocked` where none may appear. */
export function popupPageOf(path) {
    const bare = path.replace(/^\/en(?=\/|$)/, '') || '/';
    if (bare === '/')
        return 'home';
    const [first = ''] = bare.split('/').filter(Boolean);
    if (['checkout', 'orders', 'account', 'login', 'register', 'reset-password', 'newsletter'].includes(first))
        return 'blocked';
    if (first === 'products')
        return bare.split('/').filter(Boolean).length > 1 ? 'product' : 'category';
    if (first === 'categories' || first === 'brands' || first === 'search')
        return 'category';
    if (first === 'cart')
        return 'cart';
    if (first === 'blog')
        return 'blog';
    if (first === 'pages')
        return 'pages';
    return 'other';
}
/** Whether a popup may show to this visit, on this page. */
export function popupFits(popup, visit) {
    if (visit.shownThisVisit || visit.page === 'blocked')
        return false;
    if (!popup.pages.includes('all') && (visit.page === 'other' || !popup.pages.includes(visit.page)))
        return false;
    if (popup.devices !== 'all' && popup.devices !== visit.device)
        return false;
    if (popup.audience === 'new' && visit.returning)
        return false;
    if (popup.audience === 'returning' && !visit.returning)
        return false;
    const closed = visit.closed[popup.id];
    // A changed popup is a new one: its earlier close does not hold.
    if (closed && closed.version === popup.updatedAt) {
        if (popup.frequencyDays <= 0)
            return false;
        const now = (visit.now ?? new Date()).getTime();
        if (now - Date.parse(closed.at) < popup.frequencyDays * 86_400_000)
            return false;
    }
    return true;
}
/** The one popup this page view may show: the highest priority that fits, the newest of equals. */
export function choosePopup(popups, visit) {
    return (popups
        .filter((popup) => popupFits(popup, visit))
        .sort((a, b) => b.priority - a.priority || b.updatedAt.localeCompare(a.updatedAt))[0] ?? null);
}
//# sourceMappingURL=popups.js.map